// ============================================================================
// ui/stepper.js — the −/readout/+ numeric control (§2.7)
// ============================================================================
// Lifted out of design-panel.js 2026-07-25, unchanged in behaviour, because the
// Add Logo tab needed one too (Ryan moved Logo size there) and it was a closure
// inside createDesignPanel — reachable only from that panel. It never used the
// closure: `h` and its own arguments are the whole dependency list.
//
// Replaces naked sliders: −/readout/+ with a visible numeric value, tap-and-hold
// auto-repeat, and arrow-key support. `fmt` renders the value (e.g. "90%");
// `onInput(v)` receives the snapped value.
//
//   const s = createStepper({ value, min, max, step, fmt, ariaLabel, onInput, paintedAt });
//   s.setValue(0.8); // re-sync the readout WITHOUT firing onInput
//
// `paintedAt(v)` is optional: return what would actually RENDER at dial value v, and the −/+ grey
// out when the next notch would render identically. See `syncEnds` below.
//
// `setValue` is the one addition (Ryan 2026-07-25). The design panel never needed
// it: it rebuilds itself wholesale on every commit, so its steppers are recreated
// with fresh values. The Add Logo tab does NOT re-render — its DOM is built once
// and persists — so an undo, a "Reset all styling", a resume or a logo swap would
// change sizeScale underneath a stepper that carried on showing the old percentage.
// It's deliberately silent: this is "the value changed elsewhere, catch up", and
// echoing onInput would write the value straight back and can loop.
// ============================================================================

import { h } from './dom.js';
/* ⛔ THE DIAL DOES NOT GET ITS OWN OPINION ABOUT WHERE THE ART STOPS GROWING (Ryan 2026-08-06).
   `edgeOf` + `paintsSame` are the engine's, and this file used to hold a second copy of the scan
   that compared painted sizes with `!==`. render-engine gave that comparison a relative tolerance
   on 2026-08-02 — the fix Ryan approved as *"turn it on everywhere"*, worth -22% on every grip
   number — and this copy never got it, so every panel rebuild quietly re-seeded the pre-fix size
   back over the approved one. See `paintsSame`'s header for the measurement. */
import { edgeOf, paintsSame } from '../render-engine.js';

/**
 * `edgeFraction` — what this dial calls 100%, as a share of the GEOMETRIC maximum (Ryan 2026-07-30).
 *
 * Every `pctOfMax` dial has always pegged 100% to the point where the art stops growing. That is the
 * honest answer for most pieces and the wrong one where a product wants its piece to sit below its
 * own ceiling by TASTE: on the grip plate the weight number's geometric max is simply too big beside
 * the mark, which is what `GRIP_SIZE_OPEN_FACTOR` exists to say.
 *
 * The alternative — and what shipped first — was to open the dial lower, at 61% instead of 75%. It
 * paints the identical plate, and Ryan rejected it on the readout rather than the pixels: *"it simply
 * makes the weight numbers 60% and keeps the logo at 75%, as opposed to changing what a 75% weight
 * number looks like size wise."* A default that opens reading 61% looks like something already turned
 * it down and invites the buyer to put it back, undoing the correction.
 *
 * So the room moves instead of the opening share, which is the same move `GRIP_SIZE_OPEN_FACTOR`'s
 * own note describes: *"grow the room by 1/f, shrink the opening share by f, and the painted size
 * does not move."* Here it is the other direction — shrink what 100% MEANS by f, and 75% of it lands
 * exactly where the 61%-of-the-old-room did.
 *
 * ⚠ THE GEOMETRIC CEILING STAYS REACHABLE. 100% becoming a taste ceiling must not cost the buyer the
 * top of the range, so the percentage grid runs past 100 to `PCT_CEIL` (≈123% at f = 65/80) and the
 * + button still greys at the REAL wall, not the taste one. That is why `edgeRaw` is kept alongside
 * `edge`: the two answer different questions and collapsing them would silently cap the dial.
 */
/**
 * Press-and-hold to repeat, factored out of `mkBtn` so the nudge pads can wear the same behaviour
 * (Ryan 2026-08-09: *"yes, add press-and-hold repeat to the arrows"*).
 *
 * It became worth sharing the moment the logo's travel grew to three plate radii — at 0.05 a click
 * that is ~55 taps to cross a plate, and a range you can only reach by exhausting yourself is not
 * really a range. The timings are the size dials' own (380ms before it takes, then every 80ms), so
 * every repeating control in the tool feels identical.
 *
 * ⚠ THE CLICK AFTER A REPEAT IS SWALLOWED. `pointerup` is followed by a `click`, so without the
 * latch a hold would fire one extra step after the finger lifts — off by one, every time, and only
 * on the gesture where the count matters most.
 */
export function attachHoldRepeat(btn, run) {
  let hold = null;
  let rep = null;
  let didRepeat = false;
  btn.addEventListener('click', () => { if (didRepeat) { didRepeat = false; return; } run(); });
  btn.addEventListener('pointerdown', () => {
    hold = setTimeout(() => { didRepeat = true; run(); rep = setInterval(run, 80); }, 380);
  });
  const end = () => { clearTimeout(hold); if (rep) { clearInterval(rep); rep = null; } };
  btn.addEventListener('pointerup', end);
  btn.addEventListener('pointerleave', end);
  btn.addEventListener('pointercancel', end);
  return btn;
}

export function createStepper({ value, min, max, step, fmt, ariaLabel, onInput, paintedAt = null, pctOfMax = false, defaultPct = null, seedDefault = false, edgeFraction = 1 }) {
  const snap = (n) => {
    const q = Math.round((n - min) / step) * step + min;
    return Math.min(max, Math.max(min, Math.round(q * 1000) / 1000));
  };
  let v = snap(value);
  // What the readout calls 100% when `pctOfMax` is on — the dial value at which the art stops
  // growing. Seeded to the ceiling and re-derived by `refreshEdge` before every repaint of the
  // readout; declared up here because `show` closes over it and runs during construction.
  let edge = max;
  // The geometric wall itself, un-scaled by `edgeFraction`. `edge` is what the READOUT calls 100%;
  // this is where the art actually stops growing, and it is what greys the + button. Identical
  // whenever `edgeFraction` is 1, which is every dial but the grip's number.
  let edgeRaw = max;
  /* ⚠ RE-READ, NOT CAPTURED (2026-08-02). This used to be a `const` fixed at construction, which
     was true for as long as the factor depended only on the PRODUCT — a product switch always
     rebuilds the panel, so a stale copy could not exist. It now depends on the DENOMINATION too
     (the 65/80 re-peg is off on the cutout-free 2.5 / 5 lb grip plates), and the Size ladder
     deliberately does NOT rebuild the panel on a hop that keeps its layout — that rebuild is the
     card-blanking it was written to avoid. So a dial built on the 10 lb and carried onto the 5 lb
     would have gone on dividing by the 45's peg and reported a confident, wrong percentage: the
     second staleness in `wd-sizescale-is-a-share-not-a-number`, which is always the dial's cached
     edge rather than the engine's size.
       A function is accepted alongside a number so the caller can hand over the QUESTION instead of
     an answer with a shelf life — `paintedAt` already works this way, for the same reason. */
  /* ⛔ NO LONGER CLAMPED TO 1 (Ryan 2026-08-06: *"I want the 75% reading to align with the size of
     the number at 100% now"*). The clamp encoded an assumption that has stopped being true — that
     100% can only ever be a taste ceiling BELOW the geometric wall. The grip's number now opens at
     81% of its wall while still reading 75%, which needs a fraction of 1.08, and `Math.min(1, …)`
     would have silently dropped it back to 1 — leaving the DIAL seeding 75% of the wall while the
     engine seeded 81%, i.e. the exact pop that was fixed this morning, reintroduced by a clamp.
       Only the weight number's dial ever passes anything but 1 (arc text and the logo dial are both
     fixed at 1), so this widens nothing else. A fraction above 1 puts 100% past the wall, which
     `pctCeil` already handles by stopping the grid at 100, and `syncEnds` greys the + at the real
     wall — see `edgeRaw`, which is kept separately for exactly this reason. */
  const efOf = () => {
    const raw = typeof edgeFraction === 'function' ? edgeFraction() : edgeFraction;
    return raw > 0 ? raw : 1;
  };
  // ── percent-of-max mode: the dial works in WHOLE 5% STEPS of the edge ──────────────────────
  // Ryan 2026-07-25: "all increments should increase and decrease in 5% increments, not 7 or 8 or
  // anything else". Displaying `stored / edge` while stepping the STORED value by a fixed 0.05 gives
  // ragged percentages (85 → 89 → 93), because one stored notch is 5% of the ENGINE's base, not 5%
  // of the room. So in this mode the percentage is the real unit: the grid is 5, 10, … 100, and the
  // stored scale is derived from it. `edge` is what the engine says 100% is, so a step is always
  // exactly a twentieth of the available room.
  const PCT_STEP = 5;
  // How far past 100% the grid runs. 100 when 100% IS the wall (every dial but the grip's number);
  // enough to still REACH the wall when 100% is a taste ceiling below it — see `edgeFraction`.
  const pctCeil = () => { const ef = efOf(); return ef < 1 ? Math.round(100 / ef / PCT_STEP) * PCT_STEP : 100; };
  const toPct = (scale) => (edge > 0 ? (scale / edge) * 100 : 0);
  const toScale = (pct) => (pct / 100) * edge;
  const snapPct = (pct) => {
    const lo = Math.ceil((toPct(min) - 1e-6) / PCT_STEP) * PCT_STEP; // never below the stored floor
    return Math.min(pctCeil(), Math.max(lo, Math.round(pct / PCT_STEP) * PCT_STEP));
  };
  const show = () => (pctOfMax
    ? `${snapPct(toPct(v))}%`
    : (fmt ? fmt(v) : String(v)));
  const readout = h('span', { class: 'wd-step-val' }, show());
  // At the end of the range the button is DISABLED, not just inert (Ryan 2026-07-25). It was already
  // inert — `apply` returns early when the snapped value doesn't move — so a buyer at 100% could
  // click + forever and watch nothing happen, with no way to tell "this is the limit" from "this is
  // broken". Greying it out answers that without them having to guess.
  //   `disabled` rather than a class: it stops the click, the tap-and-hold repeat and keyboard
  // activation in one go, and tells a screen reader the same thing the grey tells everyone else.
  //   The epsilon matters. `snap` rounds to 3dp, so a range like 0.2–1.35 in 0.05 steps lands on
  // values that are exact enough to compare — but a float range wouldn't, and `v >= max` would then
  // stay false at the top and never grey. Comparing with a tolerance is what makes this hold for any
  // min/max/step the callers pass, including the degree and thickness dials.
  //   The RANGE is only one of the two walls, and usually not the one people hit. `paintedAt(x)`
  // is the second: an optional probe returning what would ACTUALLY be rendered at dial value x.
  // Text size is a ceiling, not a size — arc text is fitted to the span it has to live in, so past
  // a point the span is the binding constraint and raising the ceiling does nothing (Ryan
  // 2026-07-25: "it keeps increasing from 75 to 80 to 85, but the font hasn't increased once").
  // When the next notch would paint identically, that button is at the wall and greys out too.
  //   Deliberately a value probe rather than a list of rules: the panel doesn't need to know WHICH
  // constraint bit (arc span, the 72px ceiling, the top/bottom link-clamp, the slot box), and it
  // can't fall out of step with the engine when one of them changes.
  const EPS = 1e-9;
  let minusBtn = null, plusBtn = null;
  // Probe against the far END of the range, NOT the next notch. The painted size is often FLAT over
  // a stretch of the dial — a span-bound arc paints identically from ~75% to 100% — so "would one
  // more notch change anything?" answers no in BOTH directions mid-range and greys out the whole
  // control, trapping the buyer at a value they can't leave. (Caught by
  // tools/verify-size-controls-moved.mjs, which found Arch text dead at 90%.)
  //   "Is there ANY value further this way that paints differently?" is the question that actually
  // matches the words on screen. Two probes per button, cheap, and it can only disable a direction
  // that is genuinely a dead end.
  const deadEnd = (towards) => {
    if (!paintedAt) return false;
    try { return paintsSame(paintedAt(towards), paintedAt(v)); } catch { return false; } // probe trouble → never block the buyer
  };
  // A THIRD reason a button can be off: the whole control is locked by its owner, because the thing
  // it adjusts doesn't exist to adjust yet. The Add Logo tab shows Logo size while the buyer is
  // still filling in "Who's this for?", before any weight has been rendered — visible so they can
  // see what's coming, inert until there's a design under it (Ryan 2026-07-25).
  let locked = false;
  const syncEnds = () => {
    // In percent mode the ceiling is the EDGE (100%), not the stored `max` — everything above the
    // edge paints identically, so it isn't reachable and mustn't read as headroom.
    // …and the ceiling is the GEOMETRIC wall (`edgeRaw`), never the taste one. Greying + at `edge`
    // would hand the buyer a dial whose top 23% exists on the grid and refuses to move.
    const top = pctOfMax ? Math.min(max, edgeRaw) : max;
    const minusOff = locked || v <= min + EPS || deadEnd(min);
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — …AND OFF WHEN THE CLICK ITSELF CANNOT MOVE. Ryan, Drop Bear:
       *"it would keep letting me click the '+' button despite the logo being at its max size"*. Measured: the logo
       stopped at 135% (scale 1.485) with + live - `deadEnd(top)` asks whether ANY further value paints differently,
       but `nudge` steps in 5%-of-edge notches clamped to `max`, and from there no notch lands anywhere new. The button
       now asks the click's own question: would THIS press change what is painted? */
    const stuck = (sign) => { const nx = notchFrom(sign); return nx == null || (paintedAt ? (() => { try { return paintsSame(paintedAt(nx), paintedAt(v)); } catch { return false; } })() : false); };
    const plusOff = locked || v >= top - EPS || deadEnd(top) || (pctOfMax && stuck(1));
    if (minusBtn) minusBtn.disabled = minusOff;
    if (plusBtn) plusBtn.disabled = plusOff;
    // ── BOTH ENDS DEAD → THE NUMBER STOPS LOOKING ADJUSTABLE TOO (Ryan 2026-07-30) ───────────────
    // "there are various different situations where both the + and − will gray out because the thing
    // can't be adjusted at that time… can you also gray out the % number?"
    //
    // Done HERE rather than per-caller because this is the single function that decides both ends, so
    // every reason a stepper can go fully inert is already gathered in one place and gets the
    // treatment for free — the `locked` case (the control exists before the design does), a piece
    // pinned at a wall in both directions (a Badge logo at 100%, where there is no headroom above and
    // the clamp holds), and the dumbbell full-face dial, which is inert by construction. A future
    // fourth reason inherits it without knowing this exists.
    //
    // ⚠ Visual only, deliberately: both buttons already carry `disabled`, which is what tells a screen
    // reader the control is unavailable. The readout is still ACCURATE text — dimming it says "you
    // can't move this", not "ignore this" — so hiding it from assistive tech would remove real
    // information to mirror a purely visual cue.
    readout.classList.toggle('is-inert', minusOff && plusOff);
  };
  /**
   * THE EDGE: the smallest dial value that already paints at the maximum size — the point where the
   * art stops growing because it has run out of room (Ryan 2026-07-25: "100% should equal them
   * coming within a tiny margin of touching the edge").
   *
   * With `pctOfMax`, this becomes what the readout calls 100%, so the percentage answers the
   * question a buyer is actually asking — "how much of the available room am I using?" — instead of
   * "how far have I moved a multiplier whose 1.0 is an engine implementation detail?". The stored
   * value and everything the engine does with it are UNCHANGED; this is a change of units on the
   * display, which is why it can't move a single pixel of anyone's design.
   *
   * It is re-derived on every sync, and that is the point for ARC TEXT: its edge is the wrap-around
   * limit of the layout's arc, so it moves as you type. Add words and the same font no longer fits
   * the span, the edge drops, and the percentage climbs to say so — at 100% the line is filling the
   * arc exactly. (Ryan 2026-07-25: "% will obviously jump up or down depending on the amount of
   * text we type in… once we settle on our text, should we set it to 100%, the text should reach
   * its full wrap-around limits".)
   *
   * Found by scanning DOWN from the ceiling rather than solving, because each piece runs into a
   * different constraint — the logo an annulus fit, the number a slot-box width, an arc the smaller
   * of its span and the ring band — and several are min() of two rules, so there is no single
   * formula. The scan asks the engine and believes the answer.
   */
  const edgeValue = () => {
    if (!paintedAt) return max;
    // The ENGINE's scan, not a local one — see the import header. `edgeOf` walks the same
    // hi→lo grid this used to and returns `hi` when nothing ever differs, which is the same
    // "no wall found" answer as the old `return max`.
    try { return edgeOf(paintedAt, min, max, step); } catch { return max; }
  };
  /** Recompute what 100% means. Cheap, and must run before any repaint of the readout. */
  const refreshEdge = () => { if (pctOfMax) { edgeRaw = edgeValue(); edge = edgeRaw * efOf(); } };

  /**
   * Drop the dial to the SMALLEST value that paints identically (Ryan 2026-07-25: "the default
   * number on page load is larger than what it would allow you to take it to if you tried to
   * increase and click + to get to that number that way — it shouldn't be able to do this").
   *
   * Above a cap the painted size goes flat, so every value in that stretch renders the same plate.
   * A design stored anywhere in it opens showing a number you can never return to: step down once
   * and + is already at its wall, because nothing above changes anything. The readout was quoting
   * a value that had no meaning.
   *
   * Normalising picks the one value in the flat stretch that IS meaningful — the point where the
   * art stops growing — so the number on screen is the size you're actually getting and every
   * value it shows is reachable. Silent and display-only: the stored profile keeps whatever it had
   * until the buyer's next edit, which is harmless precisely because the two paint the same.
   */
  const normalizeDown = () => {
    refreshEdge();
    if (!paintedAt) return;
    try {
      const at = paintedAt(v);
      // Bounded by the step count, so a probe that returns a constant can't spin here.
      for (let guard = Math.ceil((max - min) / step) + 1; guard > 0; guard -= 1) {
        const next = snap(v - step);
        if (next >= v || next < min - EPS || !paintsSame(paintedAt(next), at)) break;
        v = next;
      }
      readout.textContent = show();
    } catch { /* probe trouble → leave the value exactly as it was */ }
  };
  const apply = (nv) => {
    const s = snap(nv);
    if (s === v) return;
    v = s; refreshEdge(); readout.textContent = show(); syncEnds(); onInput(v);
  };
  /** One 5%-of-the-edge notch (percent-of-max mode). Falls back to the plain stored step. */
  /** Where one notch would land, or null when it cannot move (WD 487 - shared by `nudge` and the + grey-out). */
  function notchFrom(sign) {
    const next = snapPct(snapPct(toPct(v)) + sign * PCT_STEP);
    const scaled = Math.min(max, Math.max(min, Math.round(toScale(next) * 1000) / 1000));
    return Math.abs(scaled - v) < 1e-9 ? null : scaled;
  }
  const nudge = (sign) => {
    if (!pctOfMax) { apply(v + sign * step); return; }
    const scaled = notchFrom(sign);
    if (scaled == null) return;
    v = scaled; refreshEdge(); readout.textContent = show(); syncEnds(); onInput(v);
  };
  const mkBtn = (sign, glyph) => {
    const btn = h('button', { type: 'button', class: 'wd-step-btn', 'aria-label': (sign < 0 ? 'Decrease ' : 'Increase ') + (ariaLabel || '') }, glyph);
    let hold = null, rep = null, didRepeat = false;
    btn.addEventListener('click', () => { if (didRepeat) { didRepeat = false; return; } nudge(sign); });
    btn.addEventListener('pointerdown', () => { hold = setTimeout(() => { didRepeat = true; nudge(sign); rep = setInterval(() => nudge(sign), 80); }, 380); });
    const end = () => { clearTimeout(hold); if (rep) { clearInterval(rep); rep = null; } };
    btn.addEventListener('pointerup', end);
    btn.addEventListener('pointerleave', end);
    btn.addEventListener('pointercancel', end);
    return btn;
  };
  minusBtn = mkBtn(-1, '−');
  plusBtn = mkBtn(1, '+');
  const wrap = h('div', { class: 'wd-stepper', role: 'group', 'aria-label': ariaLabel, tabindex: '0' }, minusBtn, readout, plusBtn);
  wrap.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') { e.preventDefault(); nudge(1); }
    else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') { e.preventDefault(); nudge(-1); }
  });
  /** Lock/unlock the whole control. Unlocking hands the ends back to the normal wall logic.
   *  (Assigned here rather than beside `syncEnds` above — `wrap` doesn't exist yet up there.) */
  wrap.setEnabled = (on) => { locked = !on; syncEnds(); };
  /** Re-sync the readout to a value set elsewhere. Silent by design — see the header. */
  wrap.setValue = (nv) => {
    const s = snap(nv);
    if (s !== v) { v = s; }
    refreshEdge();
    readout.textContent = show();
    // ALWAYS re-probe the limits, even when the value didn't move. What `paintedAt` answers depends
    // on the whole design, not just on this dial: the Add Logo tab builds its stepper before any
    // logo exists, when the probe can only answer 0 for every candidate — which reads as "no value
    // paints differently" and greys out BOTH buttons. Returning early here on an unchanged value
    // left them greyed for good once the logo landed, since the scale was still 1.
    //   Same class of staleness for text: retyping a longer word moves the arc-span wall without
    // touching the dial.
    normalizeDown();
    syncEnds();
  };
  // A control can be built already at one end (e.g. text size opens at its 100% max), and it can be
  // built inside a flat stretch where the stored number isn't reachable — normalise before the ends
  // are judged, so the two agree on the same value.
  normalizeDown();
  /**
   * SEED THE DEFAULT as a share of the room (Ryan 2026-07-25: logo and number "should load at 80%,
   * relative to the definition I gave you for where 100% should be"; text "loading at the largest
   * possible size as default… up to 100%, but sometimes less depending on the length").
   *
   * It has to happen here rather than in the profile, because the target isn't a number anyone can
   * write down in advance: 80% of the edge is a different scale for every logo aspect, every number
   * style and every digit count, and for arc text it moves with the words. The engine is the only
   * thing that knows, and `edge` is its answer.
   *
   * `seedDefault` is the caller's promise that this piece has never been sized by hand — the flag
   * lives on the element, so a buyer's choice is never overwritten by a later rebuild. Firing
   * `onInput` is what commits it, and it only fires when the value actually moves, so the rebuild
   * this triggers finds the value already correct and stops. Runs after `normalizeDown` so it seeds
   * against a settled edge.
   */
  /**
   * Seed (or re-seed) to `pct` of the edge. Exposed as well as run at construction because a
   * persistent control can be BUILT before the thing it sizes exists: the Add Logo tab creates its
   * stepper at mount, when there is no logo, no weight and therefore no edge to take a share of.
   * Seeding then would compute 80% of nothing. That tab calls this again at the beat the design goes
   * live, which is the first moment the answer means anything.
   */
  wrap.seedTo = (pct) => {
    refreshEdge();
    const target = Math.min(max, Math.max(min, Math.round(toScale(snapPct(pct)) * 1000) / 1000));
    if (!(target > 0) || Math.abs(target - v) < 1e-9) return false;
    v = target;
    readout.textContent = show();
    syncEnds();
    // `{ seeded: true }` tells the consumer this is the DEFAULT arriving, not the buyer moving the
    // dial. Every consumer marks a piece hand-sized on input — that mark is what stops a later
    // re-peg from overwriting a deliberate choice — and a seed must not claim to be one. Without
    // this the very first render freezes each piece at whatever the first layout happened to allow,
    // and nothing can re-peg it again for the rest of the session. Ryan's logo dial was already
    // undoing the flag by hand afterwards (logo-step.js); this makes that unnecessary rather than
    // asking every future caller to remember the same trick.
    onInput(v, { seeded: true });
    return true;
  };
  if (pctOfMax && seedDefault && defaultPct != null) wrap.seedTo(defaultPct);
  syncEnds();
  return wrap;
}
